<template>
  <div class="works-page">
    <div class="page-header">
      <span class="page-title">作品审核</span>
      <span class="page-count">共 {{ total }} 件作品</span>
      <el-button type="primary" size="small" icon="el-icon-plus" style="margin-left: 16px" @click="openForm(null)">新增作品</el-button>
      <div class="header-actions">
        <el-button-group class="view-toggle">
          <el-button
            size="small"
            :type="viewMode === 'grid' ? 'primary' : 'default'"
            icon="el-icon-menu"
            @click="setViewMode('grid')"
          >卡片</el-button>
          <el-button
            size="small"
            :type="viewMode === 'list' ? 'primary' : 'default'"
            icon="el-icon-s-operation"
            @click="setViewMode('list')"
          >列表</el-button>
        </el-button-group>
      </div>
    </div>

    <div class="status-tabs">
      <el-button
        v-for="tab in statusTabs"
        :key="tab.value"
        size="small"
        round
        :type="auditStatusFilter === tab.value ? 'primary' : 'default'"
        @click="changeStatusTab(tab.value)"
      >{{ tab.label }}</el-button>
      <div v-if="isApprovedTab" class="sort-toolbar">
        <span class="sort-tip">数字越小越靠前，将同步到门户展示顺序</span>
        <el-button
          v-if="sortDirty"
          type="primary"
          size="small"
          :loading="sortSaving"
          @click="saveSortOrders"
        >保存排序</el-button>
      </div>
    </div>

    <div v-if="viewMode === 'grid'" v-loading="loading" class="work-grid">
      <el-card v-for="item in list" :key="item.id" shadow="hover" class="work-card" :body-style="{ padding: '0' }">
        <div class="work-cover" @click="openDetail(item)">
          <img v-if="coverThumb(item)" :src="coverThumb(item)" alt="" class="cover-img">
          <div v-else class="cover-placeholder" :style="{ background: coverGradient(item) }">
            <i :class="coverIcon(item)" />
          </div>
          <span v-if="coverThumb(item) && isVideoWork(item)" class="cover-play-badge">
            <i class="el-icon-video-play" />
          </span>
          <div class="cover-tags">
            <el-tag size="mini" :type="auditTagType(item.auditStatus)">{{ labelOf(AUDIT_STATUS, item.auditStatus) }}</el-tag>
            <el-tag size="mini" type="info">{{ displayWorkType(item.workType) }}</el-tag>
          </div>
        </div>
        <div class="work-body">
          <div class="work-title" :title="item.title">{{ item.title }}</div>
          <div class="work-meta">
            <span><i class="el-icon-user" /> {{ item.authorName || `用户${item.userId}` }}</span>
            <span><i class="el-icon-time" /> {{ formatUserDate(item.createdAt) }}</span>
          </div>
          <el-button class="detail-btn" size="small" @click="openDetail(item)">查看详情</el-button>
          <div class="action-row">
            <el-button size="small" type="primary" plain icon="el-icon-edit" @click="openForm(item)">编辑</el-button>
            <template v-if="item.auditStatus === 0">
              <el-button type="success" size="small" plain @click="quickAudit(item, 1)">通过</el-button>
              <el-button type="danger" size="small" plain @click="quickAudit(item, 2)">驳回</el-button>
            </template>
          </div>
          <div v-if="item.auditRemark" class="audit-remark" :title="item.auditRemark">
            备注：{{ item.auditRemark }}
          </div>
        </div>
      </el-card>
    </div>

    <div v-else v-loading="loading" class="work-list">
      <el-table :data="list" stripe border size="small" class="work-table">
        <el-table-column v-if="isApprovedTab" label="排序" width="150" align="center">
          <template slot-scope="{ row, $index }">
            <div class="sort-cell">
              <el-button type="text" icon="el-icon-top" :disabled="$index === 0" @click="moveWork($index, -1)" />
              <el-input-number
                v-model="row.sortOrder"
                :min="0"
                :max="9999"
                size="mini"
                controls-position="right"
                @change="markSortDirty"
              />
              <el-button type="text" icon="el-icon-bottom" :disabled="$index === list.length - 1" @click="moveWork($index, 1)" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="封面" width="88" align="center">
          <template slot-scope="{ row }">
            <div class="list-cover" @click="openDetail(row)">
              <img v-if="coverThumb(row)" :src="coverThumb(row)" alt="" class="list-cover-img">
              <div v-else class="list-cover-placeholder" :style="{ background: coverGradient(row) }">
                <i :class="coverIcon(row)" />
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="作者" width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.authorName || `用户${row.userId}` }}</template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template slot-scope="{ row }">{{ displayWorkType(row.workType) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="88" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="auditTagType(row.auditStatus)">{{ labelOf(AUDIT_STATUS, row.auditStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" width="118">
          <template slot-scope="{ row }">{{ formatUserDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">详情</el-button>
            <el-button type="text" size="small" @click="openForm(row)">编辑</el-button>
            <template v-if="row.auditStatus === 0">
              <el-button type="text" size="small" style="color: #67c23a" @click="quickAudit(row, 1)">通过</el-button>
              <el-button type="text" size="small" style="color: #f56c6c" @click="quickAudit(row, 2)">驳回</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-empty v-if="!loading && !list.length" :description="emptyText" />

    <el-pagination
      v-if="total > pageSize"
      class="pagination"
      background
      layout="total, prev, pager, next"
      :current-page.sync="page"
      :page-size="pageSize"
      :total="total"
      @current-change="loadList"
    />

    <el-dialog
      :visible.sync="detailVisible"
      width="640px"
      top="6vh"
      custom-class="work-detail-dialog"
      @closed="handleDetailClosed"
    >
      <div v-if="detail" slot="title" class="detail-title">
        <span class="detail-icon" :style="{ background: coverGradient(detail) }">
          <i :class="coverIcon(detail)" />
        </span>
        <span>作品详情 - {{ detail.title }}</span>
      </div>

      <div v-if="detail" class="detail-body">
        <div class="media-preview">
          <video
            v-if="isVideoWork(detail) && mediaUrl(detail)"
            :key="'video-' + detail.id"
            class="media-video"
            :src="mediaUrl(detail)"
            :poster="coverThumb(detail) || undefined"
            controls
            playsinline
            preload="metadata"
          />
          <el-image
            v-else-if="isImageWork(detail) && mediaUrl(detail)"
            :src="mediaUrl(detail)"
            :preview-src-list="[mediaUrl(detail)]"
            fit="contain"
            class="media-image"
          />
          <div v-else class="media-empty">
            <i :class="coverIcon(detail)" />
            <span>暂无预览文件</span>
          </div>
        </div>

        <div class="detail-info">
          <div class="info-row">
            <span class="info-label">作品名称</span>
            <span class="info-value">{{ detail.title }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">作者</span>
            <span class="info-value">{{ detail.authorName || `用户${detail.userId}` }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">作品类型</span>
            <span class="info-value">{{ displayWorkType(detail.workType) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">审核状态</span>
            <span class="info-value">
              <el-tag size="mini" :type="auditTagType(detail.auditStatus)">
                {{ labelOf(AUDIT_STATUS, detail.auditStatus) }}
              </el-tag>
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">提交时间</span>
            <span class="info-value">{{ formatUserDate(detail.createdAt) }}</span>
          </div>
          <div v-if="detail.auditRemark" class="info-row info-row-block">
            <span class="info-label">审核备注</span>
            <span class="info-value">{{ detail.auditRemark }}</span>
          </div>
        </div>
      </div>

      <div v-if="detail && detail.auditStatus === 0" slot="footer" class="detail-footer">
        <el-button type="success" plain :loading="submitting" @click="auditFromDetail(1)">通过</el-button>
        <el-button type="danger" plain :loading="submitting" @click="auditFromDetail(2)">驳回</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="form.id ? '编辑作品' : '新增作品'"
      :visible.sync="formVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form ref="workForm" :model="form" :rules="formRules" label-width="96px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="作者用户ID">
          <el-input-number v-model="form.userId" :min="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="视频/图片URL" prop="videoUrl">
          <el-input v-model="form.videoUrl" />
        </el-form-item>
        <el-form-item label="封面URL">
          <el-input v-model="form.coverUrl" />
        </el-form-item>
        <el-form-item label="作品类型">
          <el-select v-model="form.workType" style="width: 100%">
            <el-option v-for="opt in workTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="审核状态">
          <el-select v-model="form.auditStatus" style="width: 100%">
            <el-option v-for="tab in statusTabs" :key="tab.value" :label="tab.label" :value="tab.value" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="form.auditStatus === 1" label="展示排序">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" style="width: 100%" />
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input v-model="form.auditRemark" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminWorks, auditWork, getWorkCategories, saveWork, updateWorkSortOrders } from '@/api/admin'
import { AUDIT_STATUS, workTypeDisplay, labelOf } from '@/utils/adminConstants'
import { formatUserDate } from '@/utils/userFormat'
import { resolveFileUrl, workCoverThumb } from '@/utils/fileUrl'

const COVER_GRADIENTS = {
  1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  3: 'linear-gradient(135deg, #fad961 0%, #f76b1c 100%)'
}

const COVER_ICONS = {
  1: 'el-icon-video-camera',
  2: 'el-icon-picture-outline',
  3: 'el-icon-film'
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i
const VIDEO_EXT = /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i

const VIEW_MODE_KEY = 'admin-works-view-mode'

function emptyWorkForm() {
  return {
    id: null,
    title: '',
    description: '',
    userId: 1,
    videoUrl: '',
    coverUrl: '',
    workType: 1,
    auditStatus: 0,
    auditRemark: '',
    sortOrder: 0
  }
}

export default {
  name: 'CreationsWorks',
  data() {
    return {
      AUDIT_STATUS,
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 8,
      auditStatusFilter: 0,
      viewMode: localStorage.getItem(VIEW_MODE_KEY) || 'grid',
      sortDirty: false,
      sortSaving: false,
      statusTabs: [
        { label: '待审核', value: 0 },
        { label: '已通过', value: 1 },
        { label: '已驳回', value: 2 }
      ],
      detailVisible: false,
      detail: null,
      workCategoryMap: {},
      formVisible: false,
      form: emptyWorkForm(),
      formRules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        videoUrl: [{ required: true, message: '请输入视频/图片 URL', trigger: 'blur' }]
      }
    }
  },
  computed: {
    isApprovedTab() {
      return this.auditStatusFilter === 1
    },
    emptyText() {
      const map = { 0: '暂无待审作品', 1: '暂无已通过作品', 2: '暂无已驳回作品' }
      return map[this.auditStatusFilter] || '暂无作品'
    },
    workTypeOptions() {
      return Object.keys(this.workCategoryMap).map(id => ({
        value: Number(id),
        label: this.workCategoryMap[id]
      }))
    }
  },
  created() {
    this.loadWorkCategories()
    this.loadList()
  },
  methods: {
    labelOf,
    formatUserDate,
    async loadWorkCategories() {
      try {
        const list = await getWorkCategories() || []
        this.workCategoryMap = Object.fromEntries(list.map(c => [c.id, c.name]))
      } catch (e) {
      }
    },
    displayWorkType(value) {
      if (value == null || value === '') return '-'
      if (this.workCategoryMap[value]) return this.workCategoryMap[value]
      return workTypeDisplay(value)
    },
    openForm(item) {
      this.form = item
        ? {
            id: item.id,
            title: item.title,
            description: item.description,
            userId: item.userId || 1,
            videoUrl: item.videoUrl || '',
            coverUrl: item.coverUrl || '',
            workType: item.workType || 1,
            auditStatus: item.auditStatus == null ? 0 : item.auditStatus,
            auditRemark: item.auditRemark || '',
            sortOrder: item.sortOrder == null ? 0 : item.sortOrder
          }
        : emptyWorkForm()
      this.formVisible = true
    },
    setViewMode(mode) {
      this.viewMode = mode
      localStorage.setItem(VIEW_MODE_KEY, mode)
    },
    markSortDirty() {
      this.sortDirty = true
    },
    moveWork(index, delta) {
      const target = index + delta
      if (target < 0 || target >= this.list.length) return
      const next = this.list.slice()
      const tmp = next[index]
      next[index] = next[target]
      next[target] = tmp
      next.forEach((item, i) => {
        item.sortOrder = i + 1 + (this.page - 1) * this.pageSize
      })
      this.list = next
      this.sortDirty = true
    },
    async saveSortOrders() {
      if (!this.isApprovedTab || !this.list.length) return
      this.sortSaving = true
      try {
        await updateWorkSortOrders(this.list.map(item => ({
          id: item.id,
          sortOrder: item.sortOrder == null ? 0 : item.sortOrder
        })))
        this.$message.success('排序已保存')
        this.sortDirty = false
        this.loadList()
      } catch (e) {
        this.$message.error((e && e.message) || '保存排序失败')
      } finally {
        this.sortSaving = false
      }
    },
    submitForm() {
      this.$refs.workForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await saveWork(this.form)
          this.$message.success('保存成功')
          this.formVisible = false
          this.loadList()
        } catch (e) {
          this.$message.error((e && e.message) || '保存失败')
        } finally {
          this.submitting = false
        }
      })
    },
    coverThumb(item) {
      return workCoverThumb(item)
    },
    mediaCategory(item) {
      if (this.isImageWork(item)) return 2
      if (item && item.workType === 3) return 3
      return 1
    },
    coverGradient(item) {
      return COVER_GRADIENTS[this.mediaCategory(item)] || COVER_GRADIENTS[1]
    },
    coverIcon(item) {
      return COVER_ICONS[this.mediaCategory(item)] || 'el-icon-video-camera'
    },
    auditTagType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'warning'
    },
    mediaUrl(item) {
      const url = item.videoUrl || item.coverUrl
      return url ? resolveFileUrl(url) : ''
    },
    isImageWork(item) {
      if (!item) return false
      const url = item.videoUrl || item.coverUrl || ''
      if (IMAGE_EXT.test(url)) return true
      return item.workType === 2 || item.workType === 12 || item.workType === 13
    },
    isVideoWork(item) {
      if (!item) return false
      const url = item.videoUrl || ''
      if (VIDEO_EXT.test(url)) return true
      if (this.isImageWork(item)) return false
      return true
    },
    changeStatusTab(value) {
      this.auditStatusFilter = value
      this.page = 1
      this.sortDirty = false
      this.pageSize = value === 1 ? 50 : 8
      this.loadList()
    },
    async loadList() {
      this.loading = true
      try {
        const result = await getAdminWorks({
          page: this.page,
          size: this.pageSize,
          auditStatus: this.auditStatusFilter
        })
        this.list = (result.list || []).map(item => ({
          ...item,
          sortOrder: item.sortOrder == null ? 0 : item.sortOrder
        }))
        this.total = result.total || 0
        this.sortDirty = false
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    handleDetailClosed() {
      this.detail = null
    },
    quickAudit(row, auditStatus) {
      const action = auditStatus === 1 ? '通过' : '驳回'
      this.$confirm(`确定${action}作品「${row.title}」？`, '审核确认', {
        type: auditStatus === 1 ? 'success' : 'warning'
      }).then(async () => {
        await auditWork(row.id, { auditStatus })
        this.$message.success(`已${action}`)
        this.loadList()
      }).catch(() => {})
    },
    auditFromDetail(auditStatus) {
      if (!this.detail) return
      const row = this.detail
      const action = auditStatus === 1 ? '通过' : '驳回'
      this.$confirm(`确定${action}作品「${row.title}」？`, '审核确认', {
        type: auditStatus === 1 ? 'success' : 'warning'
      }).then(async () => {
        this.submitting = true
        try {
          await auditWork(row.id, { auditStatus })
          this.$message.success(`已${action}`)
          this.detailVisible = false
          this.loadList()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.header-actions {
  margin-left: auto;
}
.view-toggle {
  vertical-align: middle;
}
.status-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.sort-toolbar {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}
.sort-tip {
  font-size: 12px;
  color: #909399;
}
.work-list {
  background: #fff;
  border-radius: 8px;
}
.work-table {
  width: 100%;
}
.list-cover {
  width: 56px;
  height: 42px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  margin: 0 auto;
  background: #f5f7fa;
}
.list-cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.list-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
}
.sort-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.sort-cell >>> .el-input-number {
  width: 88px;
}
.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.page-count {
  font-size: 13px;
  color: #909399;
}
.work-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.work-card {
  overflow: hidden;
  border-radius: 10px;
}
.work-cover {
  position: relative;
  height: 160px;
  cursor: pointer;
  overflow: hidden;
  background: #f5f7fa;
}
.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 42px;
}
.cover-play-badge {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 40px;
  background: rgba(0, 0, 0, 0.12);
  pointer-events: none;
}
.cover-tags {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.work-body {
  padding: 16px;
}
.work-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.work-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #909399;
  margin-bottom: 12px;
}
.work-meta i {
  margin-right: 4px;
}
.detail-btn {
  width: 100%;
  margin-bottom: 10px;
}
.action-row {
  display: flex;
  gap: 8px;
}
.action-row .el-button {
  flex: 1;
}
.audit-remark {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
.detail-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  padding-right: 24px;
}
.detail-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  flex-shrink: 0;
}
.detail-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.media-preview {
  border-radius: 10px;
  overflow: hidden;
  background: #000;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.media-video {
  width: 100%;
  max-height: 360px;
  display: block;
  background: #000;
}
.media-image {
  width: 100%;
  height: 320px;
  cursor: zoom-in;
}
.media-image >>> .el-image__inner {
  width: 100%;
  height: 100%;
}
.media-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #909399;
  padding: 48px 0;
  font-size: 14px;
}
.media-empty i {
  font-size: 36px;
}
.detail-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.info-row {
  display: flex;
  gap: 12px;
  font-size: 14px;
  line-height: 1.6;
}
.info-row-block {
  flex-direction: column;
  gap: 4px;
}
.info-label {
  flex-shrink: 0;
  width: 72px;
  color: #909399;
}
.info-value {
  color: #303133;
  word-break: break-all;
}
.detail-footer {
  text-align: left;
}
</style>

<style>
.work-detail-dialog .el-dialog__body {
  padding-top: 8px;
}
</style>
